import { useCallback, useEffect, useRef, useState } from 'react'

interface VerticalSplitOptions {
  /** Share of the container the top pane starts at. */
  initialRatio: number
  minTopPx: number
  minBottomPx: number
}

/**
 * Drag-to-resize split between a fixed-height top pane and a flexible bottom
 * one. The top height is kept in pixels so the map keeps its size while the
 * gantt below it scrolls.
 */
export const useVerticalSplit = ({
  initialRatio,
  minTopPx,
  minBottomPx,
}: VerticalSplitOptions) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [mapHeight, setMapHeight] = useState<number | null>(null)

  const clamp = useCallback(
    (height: number, containerHeight: number) =>
      Math.min(
        Math.max(height, minTopPx),
        Math.max(minTopPx, containerHeight - minBottomPx)
      ),
    [minBottomPx, minTopPx]
  )

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const apply = () => {
      const { height } = container.getBoundingClientRect()
      if (height > 0)
        setMapHeight(current =>
          current === null
            ? clamp(height * initialRatio, height)
            : clamp(current, height)
        )
    }

    apply()

    const observer = new ResizeObserver(apply)
    observer.observe(container)
    return () => observer.disconnect()
  }, [clamp, initialRatio])

  const startResize = useCallback(
    (event: React.PointerEvent) => {
      const container = containerRef.current
      if (!container) return

      event.preventDefault()
      const containerTop = container.getBoundingClientRect().top

      const onMove = (moveEvent: PointerEvent) => {
        const { height } = container.getBoundingClientRect()
        setMapHeight(clamp(moveEvent.clientY - containerTop, height))
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [clamp]
  )

  return { mapHeight: mapHeight ?? undefined, containerRef, startResize }
}
